import { signal, computed } from '@preact/signals'
import { resolveEffectiveSettings, type AssetWorkflowSettingsViewDto } from '@/api/workflowSettings'

interface EffectiveSettingsState {
  settings: AssetWorkflowSettingsViewDto | null
  loading: boolean
  error: string | null
  webId: string | null
}

/** Global signal holding the effective (resolved) workflow settings for the current web */
export const effectiveSettingsState = signal<EffectiveSettingsState>({
  settings: null,
  loading: false,
  error: null,
  webId: null,
})

/** True when the effective settings have at least one default reviewer configured */
export const hasConfiguredSettings = computed(() => {
  const s = effectiveSettingsState.value.settings
  return s !== null && s.DefaultReviewerUsers.length > 0
})

/**
 * Fetches the effective workflow settings for a web (always fresh, no caching).
 * Updates the global signal so any subscriber picks up the result.
 */
/** Monotonically increasing request ID to guard against stale responses */
let loadRequestId = 0

export async function loadEffectiveSettings(
  clientCompanyId: string,
  workspaceId: string,
  campaignId: string,
  webId: string
): Promise<void> {
  const requestId = ++loadRequestId

  effectiveSettingsState.value = {
    ...effectiveSettingsState.value,
    loading: true,
    error: null,
    webId,
  }

  const res = await resolveEffectiveSettings(clientCompanyId, workspaceId, campaignId, webId)

  // Guard against stale responses from rapid navigation (web A → web B)
  if (requestId !== loadRequestId) return

  if (res.data) {
    effectiveSettingsState.value = {
      settings: res.data,
      loading: false,
      error: null,
      webId,
    }
  } else {
    effectiveSettingsState.value = {
      settings: null,
      loading: false,
      error: res.error || 'Failed to load workflow settings',
      webId,
    }
  }
}

/** Reset settings (e.g., on navigation away from a web) */
export function clearEffectiveSettings(): void {
  effectiveSettingsState.value = {
    settings: null,
    loading: false,
    error: null,
    webId: null,
  }
}
